import {
  CloudServerOutlined,
  HomeOutlined,
  LinkOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { App, Button, Card, Divider, Form, Input, Space, Typography } from "antd";
import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "../auth";

interface LoginValues {
  username: string;
  password: string;
}

export function LoginPage() {
  const { login } = useAuth();
  const [, navigate] = useLocation();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [shareCode, setShareCode] = useState("");

  const handleSubmit = async (values: LoginValues) => {
    setSubmitting(true);
    try {
      await login(values.username, values.password);
      void message.success("登录成功");
    } catch (error) {
      void message.error(
        error instanceof Error ? error.message : "登录失败，请稍后重试",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openShare = () => {
    const code = shareCode.trim().toUpperCase();
    if (!/^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{12}$/.test(code)) {
      void message.error("请输入完整的 12 位分享码");
      return;
    }
    navigate(`/share/${encodeURIComponent(code)}`);
  };

  return (
    <main className="login-page">
      <section className="login-hero">
        <Space orientation="vertical" size="large">
          <CloudServerOutlined className="login-hero-icon" />
          <div>
            <Typography.Text className="login-hero-kicker">
              GreenShade Edge Storage(GSES)
            </Typography.Text>
            <Typography.Title className="login-hero-title">
              绿荫边缘存储
            </Typography.Title>
            <Typography.Paragraph className="login-hero-copy">
              绿荫社区内部存储系统，为社区成员提供统一、可靠的文件空间。
            </Typography.Paragraph>
          </div>
          <Space orientation="vertical">
            <Typography.Text className="login-feature">
              <HomeOutlined /> 该项目为绿荫社区内部存储系统
            </Typography.Text>
            <Typography.Text className="login-feature">
              <SafetyCertificateOutlined /> 该项目使用绿荫身份联盟进行认证
            </Typography.Text>
          </Space>
        </Space>
      </section>
      <section className="login-panel">
        <Card className="login-card" variant="borderless">
          <Typography.Title level={2}>欢迎回来</Typography.Title>
          <Typography.Paragraph type="secondary">
            使用绿荫身份联盟账号登录
          </Typography.Paragraph>
          <Form<LoginValues>
            layout="vertical"
            size="large"
            onFinish={(values) => void handleSubmit(values)}
            requiredMark={false}
          >
            <Form.Item
              name="username"
              label="用户名"
              rules={[{ required: true, message: "请输入用户名" }]}
            >
              <Input
                prefix={<UserOutlined />}
                autoComplete="username"
                placeholder="请输入用户名"
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: "请输入密码" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                autoComplete="current-password"
                placeholder="请输入密码"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={submitting}
            >
              登录
            </Button>
          </Form>
          <Divider plain>无需登录下载分享文件</Divider>
          <Input.Search
            value={shareCode}
            onChange={(event) =>
              setShareCode(event.target.value.replace(/\s/g, "").toUpperCase())
            }
            onSearch={openShare}
            maxLength={12}
            prefix={<LinkOutlined />}
            enterButton="打开分享"
            placeholder="输入 12 位分享码"
            size="large"
          />
        </Card>
      </section>
    </main>
  );
}
